// audit.repo — akses Supabase untuk approvals + audit_log (§26 v20).
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { currentUser } from '@core/session';

export interface ApprovalRow {
  id: number; at: string; type: string; module: string; ref_id: string; outlet: string | null;
  requester_id: number | null; requester_nama: string | null; requester_role: string | null;
  reason: string | null; risk_level: string; action_type: string | null; before_json: unknown;
  status: string; decided_by: string | null; decided_at: string | null; decision_note: string | null;
}
export interface AuditRow {
  id: number; at: string; actor_nama: string | null; actor_role: string | null; action: string;
  module: string | null; ref_id: string | null; outlet: string | null; reason: string | null;
}
export interface TicketMeta { no: string; status: string; outlet: string | null; hasil: string | null; custNama: string }

export async function getTicketMeta(no: string): Promise<TicketMeta> {
  const { data, error } = await supabase().from('tickets')
    .select('no,status,outlet,hasil,customers(nama)').eq('no', no).single();
  if (error) throw new Error(error.message);
  const r = data as { no: string; status: string; outlet: string | null; hasil: string | null; customers: { nama: string } | { nama: string }[] | null };
  const nm = (Array.isArray(r.customers) ? r.customers[0]?.nama : r.customers?.nama) ?? '—';
  return { no: r.no, status: r.status, outlet: r.outlet, hasil: r.hasil, custNama: nm };
}

export async function createApproval(d: {
  module: string; ref_id: string; outlet: string | null; reason: string;
  risk_level: string; action_type: string; before_json: unknown;
}): Promise<void> {
  const u = currentUser();
  const { error } = await supabase().from('approvals').insert({
    module: d.module, ref_id: d.ref_id, outlet: d.outlet, reason: d.reason,
    risk_level: d.risk_level, action_type: d.action_type, before_json: (d.before_json ?? null) as object | null,
    requester_id: u?.id ?? null, requester_nama: u?.nama ?? null, requester_role: u?.role ?? null,
    status: 'PENDING',
  });
  if (error) throw new Error(error.message);
}

export async function listApprovals(status = 'PENDING'): Promise<ApprovalRow[]> {
  return await fetchAllPaged<ApprovalRow>((a, b) => {
    let q = supabase().from('approvals').select('*').order('at', { ascending: false }).order('id');
    if (status !== 'ALL') q = q.eq('status', status);
    return q.range(a, b);
  }) as ApprovalRow[];
}

export async function getApproval(id: number): Promise<ApprovalRow> {
  const { data, error } = await supabase().from('approvals').select('*').eq('id', id).single();
  if (error) throw new Error(error.message);
  return data as ApprovalRow;
}

/** §26 hardening (0036): keputusan approval → RPC SECURITY DEFINER `apply_ticket_approval`.
 *  RPC verifikasi is_mgr + PENDING lock + re-derive risk dari status kini + SoD HIGH + set flag transaksi
 *  + update tiket (archived/voided/arsip_keu) + audit, SEMUA ATOMIK di DB. Direct-update TIDAK dipakai lagi
 *  (trigger guard_ticket_archive/finarchive kini menolak false→true tanpa flag RPC). Return 'APPLIED'|'REJECTED'. */
export async function applyApprovalRpc(id: number, decision: 'APPROVE' | 'REJECT', note: string): Promise<string> {
  const { data, error } = await supabase().rpc('apply_ticket_approval', { p_id: id, p_decision: decision, p_note: note || null });
  if (error) throw new Error(error.message);
  return String(data);
}

export async function listAudit(limit = 200): Promise<AuditRow[]> {
  const { data, error } = await supabase().from('audit_log')
    .select('id,at,actor_nama,actor_role,action,module,ref_id,outlet,reason').order('at', { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as AuditRow[];
}

/** Berapa baris audit lebih tua dari `sampai` (tanggal) — untuk ditunjukkan sebelum dipangkas. */
export async function countAuditSebelum(sampai: string): Promise<number> {
  const { count, error } = await supabase().from('audit_log')
    .select('id', { count: 'exact', head: true }).lt('at', `${sampai}T00:00:00Z`);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Pangkas jejak audit LAMA. Dua pagar ada di DB (policy `del_audit`: Owner + umur > 90 hari);
 * di sini hanya dikirim, jadi kalau pagarnya dilanggar yang terjadi adalah 0 baris terhapus —
 * bukan penghapusan diam-diam. Mengembalikan jumlah baris yang benar-benar hilang.
 */
export async function pangkasAudit(sampai: string): Promise<number> {
  const { data, error } = await supabase().from('audit_log')
    .delete().lt('at', `${sampai}T00:00:00Z`).select('id');
  if (error) throw new Error(error.message);
  return (data ?? []).length;
}

/** Hapus tiket oleh Owner (migrasi 0109). Otoritas & pagar uang ada DI DB, bukan di sini. */
export async function ownerDeleteTicketRpc(no: string, mode: string, reason: string): Promise<'KEEP_FIN' | 'TOTAL'> {
  const { data, error } = await supabase().rpc('owner_delete_ticket', { p_no: no, p_mode: mode, p_reason: reason });
  if (error) throw new Error(error.message);
  return data as 'KEEP_FIN' | 'TOTAL';
}

/** Jumlah permintaan yang MENUNGGU keputusan — badge di menu 🛡️ Audit & Approval, supaya tak
 *  bergantung pada notifikasi yang bisa terlewat/terbaca. RLS membatasi ke cabang yang berhak. */
export async function approvalActionCount(): Promise<number> {
  // Menu ini menampung BEBERAPA jenis permintaan yang tersimpan di tabel berbeda: hapus transaksi &
  // komisi (approvals), koreksi kehadiran (attendance), cuti (leaves), export (export_approvals).
  // Badge harus menghitung semuanya — kalau hanya `approvals`, permintaan absensi/cuti masuk tanpa
  // tanda apa pun dan menggantung berhari-hari (keluhan Apo 29 Jul). RLS tetap membatasi cakupannya.
  const sb = supabase();
  const hitung = async (jalankan: () => PromiseLike<{ count: number | null; error: unknown }>): Promise<number> => {
    try { const { count, error } = await jalankan(); return error ? 0 : (count ?? 0); } catch { return 0; }
  };
  const [appr, korr, cuti, exp] = await Promise.all([
    hitung(() => sb.from('approvals').select('id', { count: 'exact', head: true }).eq('status', 'PENDING')),
    hitung(() => sb.from('attendance').select('id', { count: 'exact', head: true }).eq('req_status', 'pending')),
    hitung(() => sb.from('leaves').select('id', { count: 'exact', head: true }).eq('status', 'pending')),
    hitung(() => sb.from('export_approvals').select('id', { count: 'exact', head: true }).eq('status', 'pending')),
  ]);
  return appr + korr + cuti + exp;
}
